import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FeatureFlag, isFeatureEnabled } from 'Utils/FeatureFlags';
import { selectLastCompletedSummary } from 'Reducers/exerciseProgressReducer';

/**
 * Return-to-roadmap navigation seam (MUS-321, Gate 3). When a sitting closes, the
 * engine emits `sessionCompleted`; the slice parks the summary
 * (`lastCompletedSummary`). This hook watches that parked summary and, on the
 * null -> non-null edge, sends the user back to the exercise pre-practice /
 * overview screen (`/exercises`, the `ExercisePrePractice` container).
 *
 * React-layer, not saga: react-router v7 exposes no saga-accessible history
 * singleton, the app navigates via `useNavigate` everywhere else, and a prior
 * attempt to `put(push(path))` from a saga is recorded as not working
 * (`Sagas/AppData.ts`). The saga's `onSessionCompleted` stays the observation
 * seam (it logs the summary); the actual route change lives here, where
 * react-router wants it. Mount this at the session surface (today the dev play
 * launcher; the Phase-3 per-session screen inherits it unchanged).
 *
 * Dark by default: no-ops unless USE_EXERCISES is on, matching every other
 * exercise-section seam. That guard is defense in depth on top of the surfaces
 * that mount this already being flag-gated.
 *
 * One-shot per completed sitting, and only for a completion observed live. The
 * summary is app-global and unpersisted, so a prior sitting's summary survives a
 * client-side route change and is still parked when the surface remounts. The hook
 * therefore arms only after it observes a `null` summary this mount, and navigates
 * only on the subsequent `null -> non-null` edge; a summary already parked at mount
 * leaves it inert (no bounce-out on remount). `navigatedRef` latches the one-shot;
 * a return to `null` (a new `sessionStarted`) re-arms both.
 */
export function useExerciseSessionReturnNavigation(target = '/exercises'): void {
  const navigate = useNavigate();
  const summary = useSelector(selectLastCompletedSummary);
  // Armed only after this mount has observed a `null` summary, so navigation fires
  // on a live `null -> non-null` transition and never on a summary that was already
  // parked at mount. `lastCompletedSummary` is app-global and unpersisted, so a prior
  // sitting's summary survives a client-side route change; without this gate, a
  // remount (Back button today, or the Phase-3 overview re-entering the session
  // screen) would bounce the user straight back to `/exercises` before they can play,
  // because `sessionStarted` (the only clearer) fires async on PhaserReady, after this
  // effect's post-commit run, and loses the race.
  const armedRef = useRef(false);
  const navigatedRef = useRef(false);

  useEffect(() => {
    if (!isFeatureEnabled(FeatureFlag.USE_EXERCISES)) return;

    if (summary == null) {
      // Open sitting (or none yet): the live baseline the completion edge rises from.
      // Arm, and clear the one-shot latch so the next completion can navigate.
      armedRef.current = true;
      navigatedRef.current = false;
      return;
    }

    // summary != null. Only navigate if we observed null this mount (a live
    // transition); a summary already parked at mount leaves us un-armed and inert.
    if (!armedRef.current) return;
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    navigate(target);
  }, [summary, navigate, target]);
}
